import { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  GraduationCap, Sparkles, ArrowRight, Zap, Brain, Target,
  Clock, ShieldCheck, Heart, Loader2, Check, Users, ThumbsUp, ThumbsDown,
  MessageCircle, School, Lock, X,
} from "lucide-react";
import { SCHOOLHUB_ENABLED } from "../lib/features";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function goToGoogleAuth() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function LoginGate({ onOpenLegal }) {
  const [loading, setLoading] = useState(false);
  const [showPwLogin, setShowPwLogin] = useState(false);
  const { checkAuth } = useAuth();

  const handleGoogleLogin = () => {
    if (loading) return;
    setLoading(true);
    goToGoogleAuth();
    // Fallback: if navigation didn't happen within 4s (rare browser edge case),
    // reset button so user can retry instead of staring at a stuck spinner.
    setTimeout(() => setLoading(false), 4000);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="w-full border-b border-slate-100 sticky top-0 bg-white/85 backdrop-blur z-10">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-[#FFD600] flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <span className="text-xl font-extrabold tracking-tight text-slate-900">SocialStart</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="header-school-login-button"
              onClick={() => setShowPwLogin(true)}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 border border-slate-200 rounded-full px-3 py-2"
              title="Konta szkół – logowanie loginem i hasłem"
            >
              <School className="w-3.5 h-3.5" /> Szkoły
            </button>
            <button
              data-testid="header-login-button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="text-sm font-bold text-slate-900 border border-slate-200 hover:border-slate-900 rounded-full px-4 py-2 disabled:opacity-60"
            >
              {loading ? "Otwieram..." : "Zaloguj się"}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-slate-100">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-12 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div className="ss-fadein">
            <div className="inline-flex items-center gap-2 bg-[#FEF9C3] border border-yellow-200 rounded-full px-3 py-1 text-xs font-semibold text-slate-900 mb-6">
              <Sparkles className="w-3.5 h-3.5" /> AI POWERED · DARMOWY START
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.03] text-slate-900">
              Nauka z AI, <br />
              <span className="relative">
                w kilka sekund.
                <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#FFD600] -z-10 rounded" />
              </span>
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-md">
              Wpisz dowolny temat. SocialStart przygotuje dla Ciebie
              streszczenie, kluczowe pojęcia i quiz sprawdzający, że
              naprawdę to rozumiesz.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <button
                data-testid="google-login-button"
                onClick={handleGoogleLogin}
                disabled={loading}
                className="inline-flex items-center gap-3 bg-[#FFD600] hover:bg-[#E6C200] disabled:bg-yellow-200 text-slate-900 font-bold text-base px-6 py-4 rounded-2xl btn-yellow-glow"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Łączę z Google...</>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Zaloguj się przez Google
                  </>
                )}
              </button>
              <div className="text-xs text-slate-500 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> 1 quiz na start · bez karty
              </div>
            </div>
            <button
              data-testid="school-password-login-button"
              onClick={() => setShowPwLogin(true)}
              className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-900 rounded-2xl px-4 py-2.5"
            >
              <School className="w-4 h-4" />
              Logowanie loginem (konta dla szkół)
            </button>
            <ul className="mt-6 grid grid-cols-2 gap-2 text-sm text-slate-600 max-w-sm">
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5"/> Po polsku</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5"/> Teoria + quiz</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5"/> Powtórki błędów</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5"/> Panel dla klasy</li>
              {SCHOOLHUB_ENABLED && (
              <li className="col-span-2 flex gap-2 items-center text-slate-700 font-semibold">
                <Users className="w-4 h-4 text-slate-900"/>
                <span>Nowość: <b>SchoolHub</b> — oceń nauczycieli ze swojej szkoły</span>
              </li>
              )}
            </ul>
          </div>

          <div className="hidden md:block ss-fadein">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#FFD600]/25 rounded-[2rem] blur-2xl" />
              <div className="relative bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.15)]">
                <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-yellow-500" /> Podgląd nauki
                </div>
                <div className="bg-[#FEF9C3] rounded-2xl p-4 mb-3">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-1">W skrócie</div>
                  <div className="text-sm text-slate-800">Fotosynteza to proces zamiany światła w cukry i tlen.</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-700"><b>Chlorofil</b><br/>zielony barwnik</div>
                  <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-700"><b>Chloroplast</b><br/>miejsce reakcji</div>
                </div>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Pytanie 3 z 10</span>
                  <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#FFD600] w-1/3" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-slate-100">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Jak to działa</div>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Trzy kroki. Zero komplikacji.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {[
              { icon: Zap, title: "Wpisz temat", desc: "Fotosynteza, II wojna, funkcje kwadratowe — cokolwiek chcesz zrozumieć." },
              { icon: Brain, title: "Dostajesz teorię", desc: "AI generuje streszczenie, pojęcia, sekcje, przykłady i rzeczy do zapamiętania." },
              { icon: Target, title: "Sprawdzasz się", desc: "10 pytań, wynik z procentem, wyjaśnienia błędów i powtórka słabych obszarów." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white border border-slate-200 rounded-3xl p-6 hover:border-slate-900 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-[#FFD600] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Krok {i + 1}</div>
                  </div>
                  <div className="mt-4 text-lg font-bold text-slate-900">{s.title}</div>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Dlaczego SocialStart</div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Uczysz się szybciej,<br/>bo materiał jest robiony pod Ciebie.
            </h2>
            <p className="mt-4 text-slate-600">
              Zamiast czytać 40 stron podręcznika, dostajesz sedno: definicje, przykłady i pytania sprawdzające. Wracasz do notatek, powtarzasz błędne pytania, mierzysz progres.
            </p>
          </div>
          <ul className="space-y-3">
            {[
              { icon: Clock, t: "Notatki w 20 sekund", d: "Zamiast godzin czytania — AI robi je za Ciebie." },
              { icon: Target, t: "Powtarzasz to, czego nie umiesz", d: "Nie tracisz czasu na to, co już opanowałeś." },
              { icon: Heart, t: "Przyjemne dla oka", d: "Czysty, minimalistyczny design pod skupienie." },
              { icon: ShieldCheck, t: "Bezpieczne dane", d: "Konto Google, bez zbędnych formularzy." },
            ].map((f, i) => {
              const Icon = f.icon;
              return (
                <li key={i} className="flex gap-3 bg-white border border-slate-200 rounded-2xl p-4">
                  <div className="w-9 h-9 rounded-xl bg-[#FEF9C3] flex-shrink-0 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-slate-900" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900">{f.t}</div>
                    <div className="text-sm text-slate-600">{f.d}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* SchoolHub teaser */}
      {SCHOOLHUB_ENABLED && (
      <section className="border-b border-slate-100 bg-white relative overflow-hidden isolate">
        {/* subtle yellow background decoration */}
        <div aria-hidden className="pointer-events-none absolute -top-32 -right-32 w-[280px] sm:w-[400px] h-[280px] sm:h-[400px] rounded-full bg-[#FFD600]/15 blur-3xl -z-10" />
        <div aria-hidden className="pointer-events-none absolute -bottom-20 -left-20 w-[220px] sm:w-[300px] h-[220px] sm:h-[300px] rounded-full bg-[#FFD600]/10 blur-3xl -z-10" />

        <div className="relative max-w-[900px] mx-auto px-4 sm:px-6 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1">
            <div className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider mb-5">
              <Sparkles className="w-3 h-3 text-[#FFD600]" /> Nowość · SchoolHub
            </div>
            <div className="inline-flex items-center gap-2 bg-[#FFD600] text-slate-900 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider mb-5 ml-2" data-testid="community-badge">
              <Users className="w-3 h-3" /> Społeczność 100 000+
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Oceń nauczyciela.<br/>
              <span className="relative inline-block">
                Zobacz, co inni myślą.
                <span className="absolute -bottom-1 left-0 right-0 h-2.5 bg-[#FFD600] -z-10 rounded" />
              </span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Pełna baza <b>55 000+ szkół i uczelni w Polsce</b> prosto z RSPO. Dodawaj
              nauczycieli i uczniów, głosuj za lub przeciw, czytaj anonimowe komentarze —
              i zobacz, jak Twoja szkoła wygląda od kuchni.
            </p>
            <ul className="mt-5 space-y-2.5 text-sm">
              <li className="flex items-start gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Wszystkie szkoły podstawowe, licea, technika, uczelnie i przedszkola
              </li>
              <li className="flex items-start gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Głosy w górę / w dół i anonimowe komentarze
              </li>
              <li className="flex items-start gap-2 text-slate-700">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                Automatyczna moderacja — bez hejtu i mowy nienawiści
              </li>
            </ul>
            <button
              data-testid="schoolhub-login-button"
              onClick={handleGoogleLogin}
              disabled={loading}
              className="mt-7 inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm px-5 py-3 rounded-2xl disabled:opacity-60"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Łączę...</> : <>Zaloguj się, żeby wejść do SchoolHub <ArrowRight className="w-4 h-4" strokeWidth={2.5}/></>}
            </button>
          </div>

          {/* Mock profile card */}
          <div className="order-1 md:order-2 ss-fadein">
            <div className="relative">
              <div className="absolute -inset-3 bg-slate-900/5 rounded-[2rem] blur-xl" />
              <div className="relative bg-white border border-slate-200 rounded-[2rem] p-5 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)]">
                {/* School header */}
                <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                  <div className="w-11 h-11 rounded-2xl bg-[#FEF9C3] flex items-center justify-center flex-shrink-0">
                    <School className="w-5 h-5 text-slate-900" strokeWidth={2.5}/>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Liceum</div>
                    <div className="font-extrabold tracking-tight text-slate-900 truncate">II LO im. Stefana Batorego</div>
                    <div className="text-xs text-slate-500">Warszawa · mazowieckie</div>
                  </div>
                </div>

                {/* Person */}
                <div className="mt-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-sm font-extrabold flex-shrink-0">
                    PK
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-bold text-slate-900">prof. Piotr K.</div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Nauczyciel</span>
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">Matematyka</div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 text-xs font-bold rounded-full px-2.5 py-1">
                        <ThumbsUp className="w-3 h-3"/> 147
                      </span>
                      <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-xs font-bold rounded-full px-2.5 py-1">
                        <ThumbsDown className="w-3 h-3"/> 12
                      </span>
                      <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 text-slate-600 text-xs font-bold rounded-full px-2.5 py-1">
                        <MessageCircle className="w-3 h-3"/> 34
                      </span>
                    </div>
                  </div>
                </div>

                {/* Comments mock */}
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span className="font-bold">Anonim · uczennica klasy 3</span>
                      <span>2 dni temu</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-snug">
                      Tłumaczy do skutku, nigdy się nie zniechęca. Najlepszy mat z całej szkoły.
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                      <span className="font-bold">Anonim · absolwent</span>
                      <span>1 tydz. temu</span>
                    </div>
                    <p className="text-sm text-slate-700 leading-snug">
                      Dzięki niemu zdałem maturę rozszerzoną na 92%. Polecam w ciemno.
                    </p>
                  </div>
                </div>

                {/* Trust bar */}
                <div className="mt-4 pt-3 border-t border-dashed border-slate-200 flex items-center gap-2 text-[11px] text-slate-500">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-600"/>
                  Moderowane · bez hejtu i mowy nienawiści
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Stats / social proof */}
      <section className="border-b border-slate-100">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-14 sm:py-16 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { v: "10", l: "pytań w każdym quizie" },
            { v: "5", l: "sekcji teorii" },
            { v: "20 s", l: "średni czas generowania" },
            { v: "PL", l: "wszystko po polsku" },
          ].map((s, i) => (
            <div key={i}>
              <div className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">{s.v}</div>
              <div className="text-xs text-slate-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-b border-slate-100">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            Gotowy, żeby zacząć?
          </h2>
          <p className="mt-4 text-slate-600 max-w-md mx-auto">
            Pierwszy quiz jest zawsze za darmo. Logowanie zajmuje sekundę.
          </p>
          <button
            data-testid="cta-login-button"
            onClick={handleGoogleLogin}
            disabled={loading}
            className="mt-8 inline-flex items-center gap-3 bg-[#FFD600] hover:bg-[#E6C200] disabled:bg-yellow-200 text-slate-900 font-bold text-base px-6 py-4 rounded-2xl btn-yellow-glow"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Łączę z Google...</>
            ) : (
              <>Zaloguj się i zacznij naukę <ArrowRight className="w-4 h-4" strokeWidth={2.5} /></>
            )}
          </button>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter onOpenLegal={onOpenLegal} />

      {/* School password login modal */}
      {showPwLogin && <SchoolLoginModal onClose={() => setShowPwLogin(false)} onSuccess={checkAuth} />}
    </div>
  );
}

/* -------------------- School password login modal -------------------- */
function SchoolLoginModal({ onClose, onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email.trim() || !password) { setError("Podaj email i hasło"); return; }
    setBusy(true);
    try {
      await api.post("/auth/login-password", { email: email.trim(), password });
      toast.success("Zalogowano");
      await onSuccess();
      // Wyloguj modal
      onClose();
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Nieprawidłowy email lub hasło");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-7 ss-fadein" onClick={(e) => e.stopPropagation()} data-testid="school-login-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#FFD600] flex items-center justify-center">
              <School className="w-5 h-5 text-slate-900" strokeWidth={2.5}/>
            </div>
            <div>
              <div className="text-lg font-extrabold tracking-tight text-slate-900">Logowanie dla szkół</div>
              <div className="text-xs text-slate-500">Konto wystawione przez administratora</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="school-login-close" className="p-2 rounded-xl hover:bg-slate-100">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed mb-5">
          Ta forma logowania jest przeznaczona dla <b>szkół i instytucji</b>, które otrzymały konto z pełnym planem <b>Szkoły</b> od administratora. Nie potrzebujesz Google — po prostu login i hasło.
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="school-login-email"
              autoComplete="username"
              autoFocus
              className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
              placeholder="np. kontakt@twojaszkola.pl"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Hasło</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="school-login-password"
              autoComplete="current-password"
              className="w-full rounded-2xl border-2 border-slate-200 focus:border-[#FFD600] outline-none px-4 py-3 text-sm"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-3 py-2" data-testid="school-login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="school-login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD600] hover:bg-[#E6C200] text-slate-900 font-bold px-6 py-3.5 rounded-2xl btn-yellow-glow disabled:opacity-50"
          >
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Loguję...</> : <>Zaloguj <ArrowRight className="w-4 h-4" strokeWidth={2.5}/></>}
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500 text-center">
          Nie masz konta szkolnego? Napisz: <a href="mailto:kontakt@socialstart.store" className="underline">kontakt@socialstart.store</a>
        </div>
      </div>
    </div>
  );
}

export function SiteFooter({ onOpenLegal }) {
  const year = new Date().getFullYear();
  const link = (tab, label, testid) => (
    <button
      data-testid={testid}
      onClick={() => onOpenLegal?.(tab)}
      className="text-slate-500 hover:text-slate-900 transition-colors text-left"
    >
      {label}
    </button>
  );
  return (
    <footer className="bg-white border-t border-slate-100">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-xl bg-[#FFD600] flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-slate-900" strokeWidth={2.5} />
              </div>
              <span className="font-extrabold tracking-tight text-slate-900">SocialStart</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Nauka z AI po polsku. Dla uczniów, studentów i klas.
            </p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Aplikacja</div>
            <ul className="space-y-2">
              <li>{link("about", "O nas", "footer-about")}</li>
              <li>{link("contact", "Kontakt", "footer-contact")}</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Prawne</div>
            <ul className="space-y-2">
              <li>{link("terms", "Regulamin", "footer-terms")}</li>
              <li>{link("privacy", "Polityka prywatności", "footer-privacy")}</li>
              <li>{link("cookies", "Polityka cookies", "footer-cookies")}</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Wsparcie</div>
            <ul className="space-y-2">
              <li>
                <a href="mailto:kontakt@socialstart.store" className="text-slate-500 hover:text-slate-900">
                  kontakt@socialstart.store
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs text-slate-400">
          <div>© {year} SocialStart. Wszelkie prawa zastrzeżone.</div>
          <div>Zbudowane po polsku · z AI</div>
        </div>
      </div>
    </footer>
  );
}
